"use client";

import Device from "@/components/device";
import { demoPages } from "@/components/live-demo-section";
import { SectionHeading } from "@/components/marketing/section-heading";
import { WheelLock } from "@/components/wheel-lock";
import {
  DeviceFrame,
  deviceFrameSize,
} from "@/components/workspace-shell/device-frame";
import { useIsomorphicLayoutEffect } from "@/hooks/use-isomorphic-layout-effect";
import { cdn } from "@/lib/cdn";
import { readableForeground } from "@/lib/handyout-palette";
import { cn } from "@/lib/utils";
import type { TOCItem } from "@/types/modules";
import type { SlideImage } from "@/utils/viewer-slide-details";
import { useInView } from "motion/react";
import {
  Columns2,
  FileText,
  ListTree,
  type LucideIcon,
} from "lucide-react";
import { useRef, useState, type CSSProperties, type ReactNode } from "react";

const AUTO_ADVANCE_MS = 5000;

const TABLET_FRAME = deviceFrameSize("tablet");

const TABLET_PALETTE = ["#A14646", "#DA6556", "#EB895B", "#FDB773", "#6B2737"];

const TABLET_PALETTE_STYLE = TABLET_PALETTE.reduce<Record<string, string>>(
  (style, hex, index) => {
    style[`--handyout-chart-${index + 1}`] = hex;
    style[`--handyout-chart-${index + 1}-fg`] = readableForeground(hex);
    return style;
  },
  { "--primary": TABLET_PALETTE[0] },
);

const TABLET_SLIDES: Record<
  string,
  { slideNumber: number; image: string; chapter: string; details: string }
> = {
  "demo-overview": {
    slideNumber: 1,
    image: cdn("homepage/tablet/slide-01.svg"),
    chapter: "Results",
    details:
      "The quarter in four numbers: **1,240** unique viewers, **87%** scroll depth beyond the fold, a median read time of **4 minutes** and **94%** thumbs-up reactions.\n\n- 12 features shipped across four quarters\n- 94% of roadmap commitments met\n- 1,200+ active presenters",
  },
  "demo-revenue": {
    slideNumber: 2,
    image: cdn("homepage/tablet/slide-02.svg"),
    chapter: "Results",
    details:
      "Monthly revenue rose from **4,200** in January to **9,800** in December and has been above target since March.\n\n- Annual revenue target reached to **94%**\n- **38 of 40** enterprise deals closed",
  },
  "demo-why": {
    slideNumber: 3,
    image: cdn("homepage/tablet/slide-03.svg"),
    chapter: "Positioning",
    details:
      "A static PDF is forgotten within a day, collects no feedback and only works one way. A handyout can be revisited at any time and carries reactions back to the presenter.",
  },
  "demo-numbers": {
    slideNumber: 4,
    image: cdn("homepage/tablet/slide-04.svg"),
    chapter: "Positioning",
    details:
      "Adoption by segment: **Education** leads with 540 users (+32%), followed by **Enterprise** with 410 (+21%). **Events** is the smallest segment at 250 users but grows fastest at **+48%**.",
  },
};

const TABLET_PAGES = demoPages
  .filter((page) => page.slideId in TABLET_SLIDES)
  .map((page) => {
    const slide = TABLET_SLIDES[page.slideId];
    return {
      ...page,
      sourceSlideNumber: slide.slideNumber,
      chapter: slide.chapter,
      details: slide.details,
    };
  });

const TABLET_SLIDE_IMAGES: Record<string, SlideImage> = Object.fromEntries(
  Object.entries(TABLET_SLIDES).map(([slideId, slide]) => [
    slideId,
    {
      url: slide.image,
      slideNumber: slide.slideNumber,
    },
  ]),
);

const TABLET_TOC: Record<string, TOCItem> = {
  results: {
    title: "Results",
    slide_range: [1, 2],
    children: {
      overview: { title: "Q4 overview", slide_range: [1, 1] },
      revenue: { title: "Revenue growth", slide_range: [2, 2] },
    },
  },
  positioning: {
    title: "Positioning",
    slide_range: [3, 4],
    children: {
      why: { title: "Why Handyout", slide_range: [3, 3] },
      numbers: { title: "By the numbers", slide_range: [4, 4] },
    },
  },
};

const BENEFITS: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: Columns2,
    title: "Slide and modules side by side",
    text: "The original slide sits on its palette colour next to the modules built from it. Tap it to see it full size.",
  },
  {
    icon: ListTree,
    title: "Jump from the table of contents",
    text: "The current section is highlighted as you read. Tap any entry to go straight to that slide.",
  },
  {
    icon: FileText,
    title: "A written summary for every slide",
    text: "Names, numbers and claims from the slide, written out next to the content instead of hidden behind a tap.",
  },
];

function useFrameScale() {
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useIsomorphicLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const measure = () => {
      const width = stage.clientWidth;
      if (width <= 0) return;
      setScale(Math.min(1, width / TABLET_FRAME.width));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  return { stageRef, scale };
}

export function TabletSection({
  badge = "Tablet view",
  className,
}: {
  badge?: ReactNode;
  className?: string;
}) {
  const { stageRef, scale } = useFrameScale();
  const inView = useInView(stageRef, { amount: 0.4 });
  const [interacted, setInteracted] = useState(false);
  const autoAdvanceMs = inView && !interacted ? AUTO_ADVANCE_MS : undefined;

  return (
    <div className={cn("flex flex-col gap-12 px-4 py-16 lg:gap-16 lg:px-6 lg:py-24", className)}>
      <SectionHeading
        badge={badge}
        title="Slide and modules, side by side."
        subtitle="On a tablet in landscape, the same link opens a two-column view: modules on the left, the original slide and its context on the right."
        breakBeforeSubtitle
      />

      <div
        ref={stageRef}
        className="relative mx-auto w-full max-w-[1100px]"
        style={
          {
            ...TABLET_PALETTE_STYLE,
            aspectRatio: `${TABLET_FRAME.width} / ${TABLET_FRAME.height}`,
          } as CSSProperties
        }
        onPointerDown={() => setInteracted(true)}
      >
        {scale > 0 ? (
          <DeviceFrame
            device="tablet"
            scale={scale}
            className="pointer-events-none select-none"
          >
            <WheelLock className="h-full w-full">
              <Device
                pages={TABLET_PAGES}
                mode="interactive"
                showDeviceMockup={false}
                autoAdvanceMs={autoAdvanceMs}
                slideImages={TABLET_SLIDE_IMAGES}
                toc={TABLET_TOC}
                handyoutTitle="Q4 Review 2025"
              />
            </WheelLock>
          </DeviceFrame>
        ) : null}
      </div>

      <div className="mx-auto grid w-full max-w-[1100px] grid-cols-1 gap-8 md:grid-cols-3">
        {BENEFITS.map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex flex-col gap-2">
            <div className="inline-flex items-center gap-2">
              <Icon className="size-4 shrink-0 text-neutral-700" />
              <h3 className="text-[15px] font-medium text-neutral-900">
                {title}
              </h3>
            </div>
            <p className="max-w-[28em] text-sm leading-relaxed text-neutral-500">
              {text}
            </p>
          </div>
        ))}
      </div>

      <p className="mx-auto w-full max-w-[1100px] text-sm text-neutral-500">
        Switches on by itself from 1024 px in landscape. No setting, no
        separate link.
      </p>
    </div>
  );
}
